import React from "react";
import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";

// Note: Replace these placeholder URLs with your actual image assets
const rollerImages = {
  hero: "https://images.unsplash.com/photo-1513694203232-719a280e022f?auto=format&fit=crop&w=1200&q=80",
  blackout: "https://images.unsplash.com/photo-1618221195710-dd6b41faaea6?auto=format&fit=crop&w=600&q=80",
  kitchen: "https://images.unsplash.com/photo-1556911220-e15024d8693c?auto=format&fit=crop&w=600&q=80",
};

function RollerBlinds() {
  return (
    <div className="max-w-7xl mx-auto px-6 py-12 text-slate-900 leading-relaxed">
      <Helmet>
        <title>Roller Blinds UK | Made to Measure Blackout & Thermal Blinds</title>
        <meta
          name="description"
          content="Premium made to measure roller blinds in the UK. Shop 2026's best blackout roller blinds, thermal window coverings, and designer patterns at MF Blinds."
        />
        <meta name="keywords" content="Roller Blinds UK, Made to Measure Blinds, Blackout Roller Blinds, Thermal Blinds, Kitchen Blinds, Modern Window Treatments" />
      </Helmet>

      {/* --- Hero Section: Focus on Visual Style --- */}
      <section className="mb-24 grid lg:grid-cols-2 gap-12 items-center">
        <div>
          <span className="text-indigo-600 font-bold tracking-widest uppercase text-sm mb-4 block">The 2026 Collection</span>
          <h1 className="text-5xl md:text-6xl font-black mb-6 leading-[1.1]">
            Custom <span className="text-indigo-600">Roller Blinds</span> UK
          </h1>
          <p className="text-xl text-slate-600 mb-8">
            Upgrade your home with the ultimate <strong>modern window treatment</strong>. Our roller blinds combine a sleek, minimal aesthetic with high-performance fabrics engineered for the modern British home.
          </p>
          <div className="flex flex-wrap gap-4">
            <button className="bg-slate-900 text-white px-10 py-4 rounded-xl font-bold hover:bg-indigo-700 transition shadow-xl">
              Shop All Patterns
            </button>
            <button className="border-2 border-slate-200 px-10 py-4 rounded-xl font-bold hover:bg-slate-50 transition">
              Order Free Samples
            </button>
          </div>
        </div>
        <div className="relative">
          <img 
            src={rollerImages.hero} 
            alt="Modern living room with stylish white roller blinds" 
            className="w-full h-[500px] object-cover rounded-[2rem] shadow-2xl"
          />
        </div>
      </section>

      {/* --- Mechanism & Installation: Technical Diagram Trigger --- */}
      <section className="mb-24 bg-slate-50 rounded-[3rem] p-12 md:p-20">
        <div className="max-w-3xl mx-auto text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">How It Works: Anatomy of a Roller Blind</h2>
          <p className="text-slate-600">
            Precision engineering meets ease of use. Understanding the components of your blind ensures a perfect fit and long-lasting operation.
          </p>
        </div>
        
        {/* DIAGRAM TRIGGER: Critical for explaining the technical assembly of the product */}
        
        
        <div className="grid md:grid-cols-3 gap-8 mt-12">
          <div className="bg-white p-8 rounded-2xl shadow-sm">
            <h4 className="font-bold text-lg mb-2">High-Torque Sidewinder</h4>
            <p className="text-sm text-slate-500">Smooth operation with precision-geared control for effortless lifting.</p>
          </div>
          <div className="bg-white p-8 rounded-2xl shadow-sm">
            <h4 className="font-bold text-lg mb-2">Aluminium Tube</h4>
            <p className="text-sm text-slate-500">Rust-proof, heavy-duty core that prevents fabric sagging on wide windows.</p>
          </div>
          <div className="bg-white p-8 rounded-2xl shadow-sm">
            <h4 className="font-bold text-lg mb-2">Universal Brackets</h4>
            <p className="text-sm text-slate-500">Top, side, or face-fixing options for total installation flexibility.</p>
          </div>
        </div>
      </section>

      {/* --- Features Grid --- */}
      <div className="grid lg:grid-cols-2 gap-16 mb-24 items-center">
        <div>
          <h2 className="text-4xl font-bold mb-8 tracking-tight">Engineered for Performance</h2>
          <div className="space-y-6">
            <div className="flex gap-4">
              <div className="w-12 h-12 bg-indigo-100 text-indigo-600 rounded-xl flex items-center justify-center text-xl shrink-0">🌙</div>
              <div>
                <h3 className="font-bold text-xl">100% Blackout Guarantee</h3>
                <p className="text-slate-600">Specialized multi-layer fabric that completely eliminates light ingress—perfect for nurseries.</p>
              </div>
            </div>
            <div className="flex gap-4">
              <div className="w-12 h-12 bg-indigo-100 text-indigo-600 rounded-xl flex items-center justify-center text-xl shrink-0">🌡️</div>
              <div>
                <h3 className="font-bold text-xl">Thermal Barrier Technology</h3>
                <p className="text-slate-600">Reflective backings keep heat inside during winter and reflect solar rays in summer.</p>
              </div>
            </div>
          </div>
        </div>
        <img 
          src={rollerImages.blackout} 
          alt="Close up of a premium charcoal grey blackout fabric" 
          className="rounded-3xl shadow-xl"
        />
      </div>

      {/* --- Measuring Logic: Diagram Trigger --- */}
      <section className="mb-24 border-t border-slate-100 pt-16">
        <h2 className="text-3xl font-bold mb-10">Measuring Your Windows</h2>
        <div className="grid md:grid-cols-2 gap-12 items-center">
          <div className="prose text-slate-600">
            <p>
              Getting the measurements right is the most important step in ordering custom blinds. You must choose between <strong>Inside Recess</strong> (the blind sits inside the window frame) or <strong>Outside Recess</strong> (the blind covers the frame).
            </p>
            <p className="mt-4">
              Always use a metal tape measure and measure in three places for both width and drop to ensure the window is square.
            </p>
          </div>
          <div className="bg-indigo-50 p-6 rounded-2xl">
            {/* DIAGRAM TRIGGER: Visualizing the difference between Recess and Exact measurements is vital for reducing customer errors */}
            
          </div>
        </div>
      </section>

      {/* --- FAQ Section --- */}
      <section className="mb-24">
        <h2 className="text-3xl font-bold mb-8 text-center">Roller Blinds FAQ</h2>
        <div className="max-w-4xl mx-auto space-y-4">
          <details className="group border border-slate-200 rounded-xl p-4">
            <summary className="font-bold cursor-pointer list-none flex justify-between items-center">
              Can I install roller blinds without a drill?
              <span className="group-open:rotate-180 transition-transform">▼</span>
            </summary>
            <p className="mt-4 text-slate-600 italic">Yes! Check out our <Link to="/perfect-fit-blinds" className="text-indigo-600 underline">Perfect Fit</Link> range for no-drill, clip-on solutions.</p>
          </details>
          <details className="group border border-slate-200 rounded-xl p-4">
            <summary className="font-bold cursor-pointer list-none flex justify-between items-center">
              Are these blinds safe for homes with pets?
              <span className="group-open:rotate-180 transition-transform">▼</span>
            </summary>
            <p className="mt-4 text-slate-600">Absolutely. All our corded blinds ship with child-safety P-clips to keep tension high and eliminate loops, fully complying with UK safety standards.</p>
          </details>
        </div>
      </section>

      {/* --- Navigation Footer --- */}
      <footer className="bg-slate-900 text-white p-12 md:p-20 rounded-[3rem] text-center">
        <h2 className="text-4xl font-bold mb-10">Explore More Collections</h2>
        <div className="flex flex-wrap justify-center gap-4">
          {["Vertical", "Roman", "Wooden", "Perfect Fit"].map((item) => (
            <Link 
              key={item}
              to={`/${item.toLowerCase().replace(" ", "-")}-blinds`} 
              className="px-8 py-3 rounded-full border border-white/20 hover:bg-white hover:text-slate-900 transition font-bold"
            >
              {item} Blinds
            </Link>
          ))}
        </div>
      </footer>
    </div>
  );
}

export default RollerBlinds;